import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AccessMatrixProvider } from "@/context/AccessMatrixContext";
import { ImpersonationProvider } from "@/context/ImpersonationContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AppShell from "@/components/layout/AppShell";
import RestaurantModuleGuard from "@/components/restaurant/RestaurantModuleGuard";
import HotelModuleGuard from "@/components/hotel/HotelModuleGuard";
import PageLoader from "@/components/common/PageLoader";

// ─── EAGER (fast-boot, first-paint critical) ──────────────────────────
// Keep the public marketing home + the login page eager so the first
// paint after a cold visit stays instant. The Marketing layout is also
// eager because it wraps all marketing sub-pages and is tiny.
import Login from "@/pages/Login";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import Home from "@/pages/marketing/Home";

// ─── LAZY (route-level code splitting) ────────────────────────────────
// Everything below is loaded on-demand via React.lazy. This slashes the
// initial bundle from ~3.6MB → ~250KB (main chunk) + N tiny chunks that
// are only fetched when the user navigates into that area.
// Iter: P1 TECHNICAL HARDENING SPRINT — 2026-02-08

const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Tenants = lazy(() => import("@/pages/Tenants"));
const CreateTenant = lazy(() => import("@/pages/CreateTenant"));
const TenantDetail = lazy(() => import("@/pages/TenantDetail"));
const Users = lazy(() => import("@/pages/Users"));
const Roles = lazy(() => import("@/pages/Roles"));
const Plans = lazy(() => import("@/pages/Plans"));
const Billing = lazy(() => import("@/pages/Billing"));
const Modules = lazy(() => import("@/pages/Modules"));
const Activity = lazy(() => import("@/pages/Activity"));
const Settings = lazy(() => import("@/pages/Settings"));

// Restaurant module pages
const RestaurantDashboard = lazy(() => import("@/pages/restaurant/Dashboard"));
const RestaurantTables = lazy(() => import("@/pages/restaurant/Tables"));
const RestaurantMenu = lazy(() => import("@/pages/restaurant/Menu"));
const POSNewOrder = lazy(() => import("@/pages/restaurant/POSNewOrder"));
const RestaurantOrders = lazy(() => import("@/pages/restaurant/Orders"));
const RestaurantKitchen = lazy(() => import("@/pages/restaurant/Kitchen"));
const RestaurantReservations = lazy(() => import("@/pages/restaurant/Reservations"));
const RestaurantTakeaway = lazy(() => import("@/pages/restaurant/Takeaway"));
const RestaurantCustomers = lazy(() => import("@/pages/restaurant/Customers"));
const RestaurantPayments = lazy(() => import("@/pages/restaurant/Payments"));
const RestaurantDailyClose = lazy(() => import("@/pages/restaurant/DailyClose"));
// Inventory / Recipes / Food Cost
const Suppliers = lazy(() => import("@/pages/restaurant/Suppliers"));
const SupplierIntelligence = lazy(() => import("@/pages/restaurant/SupplierIntelligence"));
const Ingredients = lazy(() => import("@/pages/restaurant/Ingredients"));
const InventoryDashboard = lazy(() => import("@/pages/restaurant/InventoryDashboard"));
const LotsExpiry = lazy(() => import("@/pages/restaurant/LotsExpiry"));
const Movements = lazy(() => import("@/pages/restaurant/Movements"));
const Recipes = lazy(() => import("@/pages/restaurant/Recipes"));
const RecipeVersions = lazy(() => import("@/pages/restaurant/RecipeVersions"));
const SubRecipes = lazy(() => import("@/pages/restaurant/SubRecipes"));
const RecipeEdit = lazy(() => import("@/pages/restaurant/RecipeEdit"));
const FoodCost = lazy(() => import("@/pages/restaurant/FoodCost"));
const FoodCostCategoriesPage = lazy(() => import("@/pages/restaurant/foodcost/Categories"));
const FnbIntelligence = lazy(() => import("@/pages/restaurant/foodcost/FnbIntelligence"));
import InventoryModuleGuard from "@/components/restaurant/InventoryModuleGuard";

// Public (no-auth) restaurant pages
const PublicMenu = lazy(() => import("@/pages/public/PublicMenu"));
const PublicMenuTable = lazy(() => import("@/pages/public/PublicMenuTable"));
const PublicReservations = lazy(() => import("@/pages/public/PublicReservations"));
const PublicTakeaway = lazy(() => import("@/pages/public/PublicTakeaway"));
// Demo tour (no-auth, marketing walkthrough)
const DemoTour = lazy(() => import("@/pages/demo/DemoTour"));

// Public marketing sub-pages + signup + billing
const Pricing = lazy(() => import("@/pages/marketing/Pricing"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const BillingSuccess = lazy(() => import("@/pages/billing/BillingSuccess"));
const OnboardingClaim = lazy(() => import("@/pages/onboarding/OnboardingClaim"));
const BillingDashboard = lazy(() => import("@/pages/billing/BillingDashboard"));
// Hotel Starter PMS module
const HotelDashboard = lazy(() => import("@/pages/hotel/HotelDashboard"));
const HotelToday = lazy(() => import("@/pages/hotel/Today"));
const HotelRooms = lazy(() => import("@/pages/hotel/Rooms"));
const HotelRoomTypes = lazy(() => import("@/pages/hotel/RoomTypes"));
const ChannelManagerBridge = lazy(() => import("@/pages/hotel/ChannelManagerBridge"));
const HotelReservations = lazy(() => import("@/pages/hotel/Reservations"));
const HotelCalendar = lazy(() => import("@/pages/hotel/Calendar"));
const HotelPlanner = lazy(() => import("@/pages/hotel/Planner"));
const HotelCheckIn = lazy(() => import("@/pages/hotel/CheckIn"));
const HotelCheckOut = lazy(() => import("@/pages/hotel/CheckOut"));
const HotelHousekeeping = lazy(() => import("@/pages/hotel/Housekeeping"));
const HotelPayments = lazy(() => import("@/pages/hotel/Payments"));
// Hotel Operations & Reporting (Phase 2)
const HotelReports = lazy(() => import("@/pages/hotel/Reports"));
const HotelGuests = lazy(() => import("@/pages/hotel/Guests"));
const HotelQuotes = lazy(() => import("@/pages/hotel/Quotes"));
// Hotel Enterprise (Phase 3)
const HotelSchedine = lazy(() => import("@/pages/hotel/Schedine"));
const HotelAudit = lazy(() => import("@/pages/hotel/Audit"));
const HotelNightAudit = lazy(() => import("@/pages/hotel/NightAudit"));
const HotelRevenueManagement = lazy(() => import("@/pages/hotel/RevenueManagement"));
const HotelTeam = lazy(() => import("@/pages/hotel/Team"));
import RequirePermission from "@/components/permissions/RequirePermission";
const Onboarding = lazy(() => import("@/pages/onboarding/Onboarding"));

// Enterprise pages (gated client-side + server-side)
const EnterpriseDashboard = lazy(() => import("@/pages/enterprise/EnterpriseDashboard"));
const EnterpriseLocations = lazy(() => import("@/pages/enterprise/EnterpriseLocations"));
const EnterpriseBenchmark = lazy(() => import("@/pages/enterprise/EnterpriseBenchmark"));
const EnterpriseAuditLog = lazy(() => import("@/pages/enterprise/EnterpriseAuditLog"));

// AI Financial Controller (owner/manager/admin only)
const AIController = lazy(() => import("@/pages/ai/AIController"));
const ProfitRecovery = lazy(() => import("@/pages/ai/ProfitRecovery"));
const PlaceholderModule = lazy(() => import("@/pages/PlaceholderModule"));
const LockedModulePage = lazy(() => import("@/pages/LockedModulePage"));
import ModuleRouteGuard from "@/components/auth/ModuleRouteGuard";
import OperationalGate from "@/components/subscription/OperationalGate";

// Legal pages (public viewer + admin + tenant docs)
const LegalView = lazy(() => import("@/pages/legal/LegalView"));
const LegalAdmin = lazy(() => import("@/pages/admin/LegalAdmin"));
const HelpCenter = lazy(() => import("@/pages/HelpCenter"));
const Glossary = lazy(() => import("@/pages/help/Glossary"));
const TenantIntegrations = lazy(() => import("@/pages/TenantIntegrations"));
const FEAPIIntegrationWizard = lazy(() => import("@/pages/settings/FEAPIIntegrationWizard"));
const PermissionsMatrix = lazy(() => import("@/pages/PermissionsMatrix"));
const LegalDocuments = lazy(() => import("@/pages/billing/LegalDocuments"));

// Platform Owner Control Tower (SaaS operations homepage)
const ControlTower = lazy(() => import("@/pages/platform/ControlTower"));
const Observability = lazy(() => import("@/pages/platform/Observability"));
const PlatformTrials = lazy(() => import("@/pages/platform/Trials"));
const TrialLifecycle = lazy(() => import("@/pages/platform/TrialLifecycle"));
const PlatformBilling = lazy(() => import("@/pages/platform/PlatformBilling"));
const ActivationRequestsInbox = lazy(() => import("@/pages/platform/ActivationRequestsInbox"));
const PlatformOperationsLog = lazy(() => import("@/pages/platform/OperationsLog"));
const PlatformCommunications = lazy(() => import("@/pages/platform/PlatformCommunications"));
const PlatformSmsPacks = lazy(() => import("@/pages/platform/PlatformSmsPacks"));
const PlatformSecrets = lazy(() => import("@/pages/platform/PlatformSecrets"));
const PlatformFiscalPilots = lazy(() => import("@/pages/platform/PlatformFiscalPilots"));
const PlatformWhatsAppProviders = lazy(() => import("@/pages/platform/PlatformWhatsAppProviders"));
const CashFlow = lazy(() => import("@/pages/finance/CashFlow"));
const FinancialIntelligence = lazy(() => import("@/pages/finance/FinancialIntelligence"));
const MultiSiteDashboard = lazy(() => import("@/pages/direzione/MultiSiteDashboard"));
const MBOManagement = lazy(() => import("@/pages/direzione/MBOManagement"));
import RedirectPlatformToControlTower from "@/components/auth/RedirectPlatformToControlTower";
import RequirePlatformRole from "@/components/auth/RequirePlatformRole";

// Phase 11B / 11C.2 / 12E — new operator pages
const InventoryV2 = lazy(() => import("@/pages/restaurant/InventoryV2"));
const BeverageIntelligence = lazy(() => import("@/pages/restaurant/BeverageIntelligence"));
// Fase 2 — F&B Cost Engine
const PurchaseOrders = lazy(() => import("@/pages/restaurant/PurchaseOrders"));
const SupplierInvoiceImport = lazy(() => import("@/pages/restaurant/SupplierInvoiceImport"));
const PhysicalInventory = lazy(() => import("@/pages/restaurant/PhysicalInventory"));
const HandheldWaiter = lazy(() => import("@/pages/restaurant/HandheldWaiter"));
const WineCellar = lazy(() => import("@/pages/restaurant/WineCellar"));
const FnbPeriods = lazy(() => import("@/pages/restaurant/FnbPeriods"));
const FnbKpiDashboard = lazy(() => import("@/pages/restaurant/FnbKpiDashboard"));
const BreakEven = lazy(() => import("@/pages/restaurant/BreakEven"));
const QrMenu = lazy(() => import("@/pages/restaurant/QrMenu"));
const RecipeWizard = lazy(() => import("@/pages/restaurant/RecipeWizard"));
const GdprSettings = lazy(() => import("@/pages/GdprSettings"));
const FinanceDashboard = lazy(() => import("@/pages/FinanceDashboard"));
const ExecutiveFinanceDashboard = lazy(() => import("@/pages/ExecutiveFinanceDashboard"));
const ReportsHub = lazy(() => import("@/pages/ReportsHub"));
const AiInsightsV2 = lazy(() => import("@/pages/AiInsightsV2"));
const AiActionCenter = lazy(() => import("@/pages/AiActionCenter"));
const IntegrationsRegistry = lazy(() => import("@/pages/IntegrationsRegistry"));
const ComboEndOfDay = lazy(() => import("@/pages/combo/EndOfDay"));
const AuditTrailEnterprise = lazy(() => import("@/pages/AuditTrailEnterprise"));
const CustomRoles = lazy(() => import("@/pages/security/CustomRoles"));
const PermissionInspector = lazy(() => import("@/pages/security/PermissionInspector"));
const FinancialPlanning = lazy(() => import("@/pages/finance/FinancialPlanning"));
const BudgetV2 = lazy(() => import("@/pages/finance/BudgetV2"));
const ExecutiveFinancial = lazy(() => import("@/pages/finance/ExecutiveFinancial"));
const FinancialStatements = lazy(() => import("@/pages/finance/FinancialStatements"));
const PeriodClosures = lazy(() => import("@/pages/finance/PeriodClosures"));
const CashShift = lazy(() => import("@/pages/finance/CashShift"));
const FinancialCompliance = lazy(() => import("@/pages/finance/FinancialCompliance"));
const ApprovalCenter = lazy(() => import("@/pages/governance/ApprovalCenter"));
const AccountingCodes = lazy(() => import("@/pages/finance/AccountingCodes"));
const OperationalCommandCenter = lazy(() => import("@/pages/platform/OperationalCommandCenter"));
const IntelligenceDashboard = lazy(() => import("@/pages/bi/IntelligenceDashboard"));
const OperationsCenter = lazy(() => import("@/pages/operations/OperationsCenter"));
const ExecutiveAlertsConfig = lazy(() => import("@/pages/operations/ExecutiveAlertsConfig"));
const DataActivationCenter = lazy(() => import("@/pages/operations/DataActivationCenter"));
const SetupWizard = lazy(() => import("@/pages/onboarding/SetupWizard"));
const SetupPlaceholder = lazy(() => import("@/pages/SetupPlaceholder"));
const PrintersAndDevices = lazy(() => import("@/pages/settings/PrintersAndDevices"));
const BillingDocuments = lazy(() => import("@/pages/settings/BillingDocuments"));
const FiscalProfile = lazy(() => import("@/pages/settings/FiscalProfile"));
const FinanceAutomations = lazy(() => import("@/pages/finance/FinanceAutomations"));
const NightAuditReconciliation = lazy(() => import("@/pages/hotel/NightAuditReconciliation"));
const GuestCommunications = lazy(() => import("@/pages/settings/GuestCommunications"));
const SetupGuide = lazy(() => import("@/pages/settings/SetupGuide"));
const LegalDocumentsSettings = lazy(() => import("@/pages/settings/LegalDocuments"));
const HelpForms = lazy(() => import("@/pages/help/HelpForms"));
const Distribution = lazy(() => import("@/pages/settings/Distribution"));

// Marketing sub-pages (lazy — only fetched when user visits them)
const Features = lazy(() => import("@/pages/marketing/Features"));
const Restaurant = lazy(() => import("@/pages/marketing/Restaurant"));
const Hotel = lazy(() => import("@/pages/marketing/Hotel"));
const Combo = lazy(() => import("@/pages/marketing/Combo"));
const Enterprise = lazy(() => import("@/pages/marketing/Enterprise"));
const FAQ = lazy(() => import("@/pages/marketing/FAQ"));
const Contact = lazy(() => import("@/pages/marketing/Contact"));
const WhatsAppBrevo = lazy(() => import("@/pages/marketing/WhatsAppBrevo"));
const DemoPage = lazy(() => import("@/pages/marketing/Demo"));

const guarded = (Component) => (
  <RestaurantModuleGuard>
    <Component />
  </RestaurantModuleGuard>
);

const hotelGuarded = (Component, perm) => (
  <HotelModuleGuard>
    {perm ? (
      <RequirePermission perm={perm}>
        <Component />
      </RequirePermission>
    ) : (
      <Component />
    )}
  </HotelModuleGuard>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AccessMatrixProvider>
          <ImpersonationProvider>
          <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Public restaurant pages — slug-based, no auth */}
            <Route path="/r/:slug/menu" element={<PublicMenu />} />
            <Route path="/r/:slug/t/:token" element={<PublicMenuTable />} />
            <Route path="/r/:slug/reservations" element={<PublicReservations />} />
            {/* Friendly alias requested by ops: same page, simpler URL */}
            <Route path="/booking/:slug" element={<PublicReservations />} />
            <Route path="/r/:slug/takeaway" element={<PublicTakeaway />} />
            <Route path="/r/:slug" element={<Navigate to="menu" replace />} />

            {/* Demo Tour — no auth, marketing walkthrough */}
            <Route path="/demo-tour" element={<DemoTour />} />

            {/* Public marketing + auth flows (no auth required) */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/features" element={<Features />} />
              <Route path="/restaurant" element={<Restaurant />} />
              <Route path="/hotel" element={<Hotel />} />
              <Route path="/combo" element={<Combo />} />
              <Route path="/enterprise" element={<Enterprise />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/whatsapp-business-brevo" element={<WhatsAppBrevo />} />
            </Route>
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/billing/success" element={<BillingSuccess />} />
            <Route path="/onboarding/success" element={<BillingSuccess />} />
            <Route path="/onboarding/claim" element={<OnboardingClaim />} />
            {/* Iter 85 Fase 7 — legacy post-signup welcome wizard moved to a
                non-conflicting slug so /onboarding can host the new SetupWizard. */}
            <Route path="/onboarding/welcome" element={<Onboarding />} />

            {/* Legal documents — canonical + SEO aliases */}
            <Route path="/legal/:type" element={<LegalView />} />
            <Route path="/terms" element={<LegalView fixedType="terms" />} />
            <Route path="/privacy" element={<LegalView fixedType="privacy" />} />
            <Route path="/cookies" element={<LegalView fixedType="cookies" />} />
            <Route path="/refund-policy" element={<LegalView fixedType="refund" />} />

            {/* Authed routes (with shell) */}
            <Route
              element={
                <ProtectedRoute>
                  <OperationalGate>
                    <AppShell />
                  </OperationalGate>
                </ProtectedRoute>
              }
            >
              {/* Platform Owner Control Tower — owner-only homepage */}
              <Route
                path="/platform"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <ControlTower />
                  </ProtectedRoute>
                }
              />
              {/* Fase 8.8 — Trial Management (platform-only) */}
              <Route
                path="/platform/trials"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <PlatformTrials />
                  </ProtectedRoute>
                }
              />
              {/* Trial Lifecycle & Conversion Automation V1 — DRY-RUN only */}
              <Route
                path="/platform/trial-lifecycle"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <TrialLifecycle />
                  </ProtectedRoute>
                }
              />
              {/* Iter 76 — Platform Owner Billing (platform_owner write,
                  platform_admin view-only enforced server-side) */}
              <Route
                path="/platform/billing"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <PlatformBilling />
                  </ProtectedRoute>
                }
              />
              {/* Iter 83 — Platform Activation Requests inbox */}
              <Route
                path="/platform/activation-requests"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <ActivationRequestsInbox />
                  </ProtectedRoute>
                }
              />
              {/* Iter Fiscal Aruba Pilot — owner-only fiscal pilot management */}
              <Route
                path="/platform/fiscal-pilots"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <PlatformFiscalPilots />
                  </ProtectedRoute>
                }
              />
              {/* Iter WhatsApp P3 — WhatsApp Business provider foundation (owner) */}
              <Route
                path="/platform/whatsapp-providers"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <PlatformWhatsAppProviders />
                  </ProtectedRoute>
                }
              />
              {/*
                Architectural boundary: platform users see Control Tower,
                tenants see Dashboard. The same URLs are wrapped in
                RedirectPlatformToControlTower so platform_owner cannot
                accidentally land on a tenant-commercial UX (pricing,
                checkout, billing dashboard etc.).
              */}
              <Route
                path="/dashboard"
                element={
                  <RedirectPlatformToControlTower>
                    <Dashboard />
                  </RedirectPlatformToControlTower>
                }
              />
              <Route path="/tenants" element={<Tenants />} />
              <Route
                path="/tenants/new"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <CreateTenant />
                  </ProtectedRoute>
                }
              />
              <Route path="/tenants/:tenantId" element={<TenantDetail />} />
              <Route path="/users" element={<Users />} />
              <Route path="/roles" element={<Roles />} />
              <Route
                path="/plans"
                element={
                  <RedirectPlatformToControlTower>
                    <Plans />
                  </RedirectPlatformToControlTower>
                }
              />
              <Route
                path="/billing"
                element={
                  <RedirectPlatformToControlTower>
                    <BillingDashboard />
                  </RedirectPlatformToControlTower>
                }
              />
              <Route path="/billing/documents" element={<LegalDocuments />} />
              <Route path="/billing/legacy" element={<Billing />} />
              <Route path="/modules" element={<Modules />} />
              <Route path="/activity" element={<Activity />} />
              <Route path="/settings" element={<Settings />} />
              {/* Iter 71 — Help Center post-login (any authenticated user) */}
              <Route path="/help" element={<HelpCenter />} />
              {/* Iter 74 — KPI Glossary deep-linked from in-app tooltips */}
              <Route path="/help/glossary" element={<Glossary />} />
              {/* Iter 71 — Tenant-scoped integrations placeholder page.
                  The platform-wide Integrations Registry is at /platform/integrations
                  and is platform-only; this surface shows ONLY tenant-scoped
                  providers + roadmap badges, never platform secrets. */}
              <Route path="/settings/integrations" element={<TenantIntegrations />} />
              <Route path="/settings/integrations/fatturazione-elettronica-api" element={<FEAPIIntegrationWizard />} />
              {/* Iter 66 Track A Hotfix — Printers & Devices foundation.
                  Accessible to every tenant business_type / plan; the
                  backend gates writes by tenant_id + role. */}
              <Route path="/settings/printers" element={<PrintersAndDevices />} />
              <Route path="/settings/billing-documents" element={<BillingDocuments />} />
              <Route path="/settings/fiscal-profile" element={<FiscalProfile />} />
              <Route path="/finance/automations" element={<FinanceAutomations />} />
              <Route path="/hotel/night-audit/reconciliation" element={<NightAuditReconciliation />} />
              <Route path="/settings/guest-communications" element={
                <RequirePermission perm="guest_communications_view">
                  <GuestCommunications />
                </RequirePermission>
              } />
              <Route path="/settings/distribution" element={
                <RequirePermission perm="distribution_view">
                  <Distribution />
                </RequirePermission>
              } />
              {/* Iter 78 — Setup Guide / Onboarding Center (tenant-scoped) */}
              <Route path="/settings/setup-guide" element={<SetupGuide />} />
              <Route path="/settings/legal-documents" element={<LegalDocumentsSettings />} />
              {/* Iter 79 — Help / Forms training (open to all authenticated tenants) */}
              <Route path="/help/forms" element={<HelpForms />} />
              {/* Iter 73 — Granular Permissions Editor (manage_team required, enforced backend-side) */}
              <Route path="/team/permissions" element={<PermissionsMatrix />} />
              <Route
                path="/admin/legal"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <LegalAdmin />
                  </ProtectedRoute>
                }
              />

              {/* Restaurant module — guarded */}
              <Route path="/restaurant/dashboard" element={guarded(RestaurantDashboard)} />
              <Route path="/restaurant/tables" element={guarded(RestaurantTables)} />
              <Route path="/restaurant/menu" element={guarded(RestaurantMenu)} />
              <Route path="/restaurant/qr-menu" element={guarded(QrMenu)} />
              <Route path="/restaurant/pos/new" element={guarded(POSNewOrder)} />
              <Route path="/restaurant/handheld" element={guarded(HandheldWaiter)} />
              <Route path="/restaurant/orders" element={guarded(RestaurantOrders)} />
              <Route path="/restaurant/kitchen" element={guarded(RestaurantKitchen)} />
              <Route path="/restaurant/reservations" element={guarded(RestaurantReservations)} />
              <Route path="/restaurant/takeaway" element={guarded(RestaurantTakeaway)} />
              <Route path="/restaurant/customers" element={guarded(RestaurantCustomers)} />
              <Route path="/restaurant/payments" element={guarded(RestaurantPayments)} />
              <Route path="/restaurant/daily-close" element={guarded(RestaurantDailyClose)} />

              {/* Hotel Starter PMS — server gated by hotel_core module (402 if not in plan) + RBAC permission (403 forbidden state if not in role) */}
              <Route path="/hotel" element={hotelGuarded(HotelToday)} />
              <Route path="/hotel/today" element={hotelGuarded(HotelToday)} />
              <Route path="/hotel/dashboard" element={hotelGuarded(HotelDashboard)} />
              <Route path="/hotel/rooms" element={hotelGuarded(HotelRooms, "manage_room_status")} />
              <Route path="/hotel/room-types" element={hotelGuarded(HotelRoomTypes, "manage_rooms")} />
              <Route path="/hotel/reservations" element={hotelGuarded(HotelReservations, "manage_reservations")} />
              <Route path="/hotel/calendar" element={hotelGuarded(HotelCalendar, "manage_reservations")} />
              <Route path="/hotel/planner" element={hotelGuarded(HotelPlanner, "manage_reservations")} />
              <Route path="/hotel/checkin" element={hotelGuarded(HotelCheckIn, "manage_checkin")} />
              <Route path="/hotel/checkout" element={hotelGuarded(HotelCheckOut, "manage_checkout")} />
              <Route path="/hotel/housekeeping" element={hotelGuarded(HotelHousekeeping, "manage_housekeeping")} />
              <Route path="/hotel/payments" element={hotelGuarded(HotelPayments, "manage_payments")} />
              <Route path="/hotel/reports" element={hotelGuarded(HotelReports, "view_reports")} />
              <Route path="/hotel/guests" element={hotelGuarded(HotelGuests, "manage_guests")} />
              <Route path="/hotel/quotes" element={hotelGuarded(HotelQuotes, "manage_reservations")} />
              <Route path="/hotel/schedine" element={hotelGuarded(HotelSchedine, "manage_schede")} />
              <Route path="/hotel/audit" element={hotelGuarded(HotelAudit, "view_audit")} />
              <Route path="/hotel/night-audit" element={hotelGuarded(HotelNightAudit, "manage_night_audit")} />
              {/* Iter 91 Fase 5 — Revenue Management foundation (Hotel Pro / Combo Premium / Enterprise + hotel_core) */}
              <Route path="/hotel/revenue-management" element={hotelGuarded(HotelRevenueManagement)} />
              <Route path="/hotel/team" element={hotelGuarded(HotelTeam, "manage_team")} />
              {/* Channel Manager Bridge — foundation API-ready (mock/sandbox) */}
              <Route path="/hotel/distribution/channel-manager" element={hotelGuarded(ChannelManagerBridge, "distribution_view")} />

              {/* Iter 92 — Cash Flow Ledger (tenant finance, cross-vertical) */}
              <Route path="/finance/cash-flow" element={
                <RequirePermission perm="view_financials">
                  <CashFlow />
                </RequirePermission>
              } />

              {/* Sprint A — Intelligence Finanziaria (Executive Dashboard, P&L, Variance, CFF, Dept Profitability) */}
              <Route path="/finance/intelligence" element={
                <RequirePermission perm="view_financials">
                  <FinancialIntelligence />
                </RequirePermission>
              } />

              {/* Sprint D — Direzione Multi-Sede + MBO */}
              <Route path="/direzione/multi-site" element={
                <RequirePermission perm="view_financials">
                  <MultiSiteDashboard />
                </RequirePermission>
              } />
              <Route path="/direzione/mbo" element={
                <RequirePermission perm="view_financials">
                  <MBOManagement />
                </RequirePermission>
              } />

              {/* Iter 75 Fase 2 — Combo End-of-Day (visible only to combo tenants by permission gate) */}
              <Route path="/combo/end-of-day" element={<ComboEndOfDay />} />

              {/* Iter 76 Fase 3 — Audit Trail Enterprise (unified cross-vertical) */}
              <Route
                path="/audit-trail"
                element={
                  <ModuleRouteGuard module="audit_trail_enterprise">
                    <AuditTrailEnterprise />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 77 Fase 4 — Custom Roles + Permission Inspector */}
              <Route
                path="/security/custom-roles"
                element={
                  <ModuleRouteGuard module="audit_trail_enterprise">
                    <CustomRoles />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/security/permission-inspector"
                element={
                  <ModuleRouteGuard module="audit_trail_enterprise">
                    <PermissionInspector />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 78 Fase 5 — Financial Operations Enterprise */}
              <Route
                path="/finance/planning"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <FinancialPlanning />
                  </ModuleRouteGuard>
                }
              />
              {/* Iter 92.7 — Budget v2 (Cash Basis SSOT) */}
              <Route
                path="/finance/budget-v2"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <BudgetV2 />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/finance/executive-financial"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <ExecutiveFinancial />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/finance/statements"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <FinancialStatements />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/finance/periods"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <PeriodClosures />
                  </ModuleRouteGuard>
                }
              />
              {/* Iter 53 Fase 3B B1 — Cash Shift (cross-vertical, permission-only) */}
              <Route path="/finance/cash-shifts" element={<CashShift />} />
              {/* Iter 55/56 Fase 3B Compliance Pack — universal, permission-only */}
              <Route path="/finance/compliance" element={<FinancialCompliance />} />
              {/* Iter 58 Fase 3B Final — Approval Center (universal, permission-only) */}
              <Route path="/governance/approvals" element={<ApprovalCenter />} />
              {/* Iter 59 Fase 3B Final Polish — Accounting Codes (universal, permission-only) */}
              <Route path="/finance/accounting-codes" element={<AccountingCodes />} />
              {/* Iter 61 Track A — Operational Command Center (platform_owner) */}
              <Route path="/platform/command-center" element={<OperationalCommandCenter />} />
              <Route
                path="/bi/dashboard"
                element={
                  <ModuleRouteGuard module="intelligence_bi">
                    <IntelligenceDashboard />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 80 Fase 6 — Operations Intelligence Center 2.0 */}
              <Route
                path="/operations-center"
                element={
                  <ModuleRouteGuard module="intelligence_bi">
                    <OperationsCenter />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 81 Fase 6.1 — Weekly Executive Alerts */}
              <Route
                path="/operations/executive-alerts"
                element={
                  <ModuleRouteGuard module="intelligence_bi">
                    <ExecutiveAlertsConfig />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 82 Fase 6.2 — Data Activation Center */}
              <Route
                path="/operations/data-activation"
                element={
                  <ModuleRouteGuard module="intelligence_bi">
                    <DataActivationCenter />
                  </ModuleRouteGuard>
                }
              />

              {/* Iter 85 Fase 7 — Guided Onboarding v2 (Setup Wizard) */}
              <Route path="/onboarding" element={<SetupWizard />} />
              <Route path="/setup/:sectionId" element={<SetupPlaceholder />} />

              {/* Inventory / Recipes / Food Cost — gated by their own modules */}
              <Route
                path="/restaurant/suppliers"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <Suppliers />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/supplier-intelligence"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <SupplierIntelligence />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/ingredients"
                element={
                  <InventoryModuleGuard
                    requiredModules={["inventory", "recipes", "food_cost"]}
                  >
                    <Ingredients />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/inventory"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <InventoryDashboard />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/movements"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <Movements />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/lots-expiry"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <LotsExpiry />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/recipes"
                element={
                  <InventoryModuleGuard
                    requiredModules={["recipes", "food_cost"]}
                  >
                    <Recipes />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/recipes/new"
                element={
                  <InventoryModuleGuard requiredModules={["recipes"]}>
                    <RecipeEdit />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/recipes/:recipeId/edit"
                element={
                  <InventoryModuleGuard requiredModules={["recipes"]}>
                    <RecipeEdit />
                  </InventoryModuleGuard>
                }
              />
              {/* Phase 2 closure — recipe versioning + sub-recipes */}
              <Route
                path="/restaurant/recipes/:recipeId/versions"
                element={
                  <InventoryModuleGuard requiredModules={["recipes"]}>
                    <RecipeVersions />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/sub-recipes"
                element={
                  <InventoryModuleGuard requiredModules={["recipes"]}>
                    <SubRecipes />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/food-cost"
                element={
                  <InventoryModuleGuard
                    requiredModules={["food_cost", "recipes"]}
                  >
                    <FoodCost />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/food-cost/categories"
                element={
                  <InventoryModuleGuard
                    requiredModules={["food_cost", "recipes"]}
                  >
                    <FoodCostCategoriesPage />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/food-cost/intelligence"
                element={
                  <InventoryModuleGuard
                    requiredModules={["food_cost", "recipes"]}
                  >
                    <FnbIntelligence />
                  </InventoryModuleGuard>
                }
              />

              {/* Phase 11B — Inventory Movements v2 (multi-warehouse) */}
              <Route
                path="/restaurant/inventory/movements"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <InventoryV2 />
                  </InventoryModuleGuard>
                }
              />

              {/* Phase 11C.2 — Beverage Intelligence (pour cost, glass cost, alerts) */}
              <Route
                path="/restaurant/beverage"
                element={
                  <InventoryModuleGuard requiredModules={["food_cost"]}>
                    <BeverageIntelligence />
                  </InventoryModuleGuard>
                }
              />

              {/* Fase 2 — F&B Cost Engine */}
              <Route
                path="/restaurant/purchase-orders"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <PurchaseOrders />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/supplier-invoice-import"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <SupplierInvoiceImport />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/physical-inventory"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <PhysicalInventory />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/wine-cellar"
                element={
                  <InventoryModuleGuard requiredModules={["inventory"]}>
                    <WineCellar />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/recipes/wizard"
                element={
                  <InventoryModuleGuard requiredModules={["recipes", "food_cost"]}>
                    <RecipeWizard />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/fnb/kpi"
                element={
                  <InventoryModuleGuard requiredModules={["food_cost"]}>
                    <FnbKpiDashboard />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/fnb/periods"
                element={
                  <InventoryModuleGuard requiredModules={["food_cost"]}>
                    <FnbPeriods />
                  </InventoryModuleGuard>
                }
              />
              <Route
                path="/restaurant/break-even"
                element={
                  <InventoryModuleGuard requiredModules={["food_cost", "break_even"]}>
                    <BreakEven />
                  </InventoryModuleGuard>
                }
              />

              {/* Phase 12E — GDPR / Compliance / Data Retention */}
              <Route path="/settings/gdpr" element={<GdprSettings />} />

              {/* Phase 12F — Finance Dashboard + Reports Hub */}
              <Route
                path="/finance"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <FinanceDashboard />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/finance/executive"
                element={
                  <ModuleRouteGuard module="executive_financial">
                    <ExecutiveFinanceDashboard />
                  </ModuleRouteGuard>
                }
              />
              <Route path="/reports" element={<ReportsHub />} />

              {/* Phase 2 — AI Layer v2 (deterministic insights) */}
              <Route path="/ai/insights" element={<AiInsightsV2 />} />
              <Route path="/ai/action-center" element={<AiActionCenter />} />

              {/* Phase 2 — Integrations Registry (Platform Owner / Admin only) */}
              <Route
                path="/platform/integrations"
                element={
                  <ProtectedRoute roles={["platform_owner", "platform_admin"]}>
                    <IntegrationsRegistry />
                  </ProtectedRoute>
                }
              />

              {/* Phase 12D — Observability (Platform Owner / Admin only) */}
              <Route
                path="/platform/observability"
                element={
                  <RequirePlatformRole>
                    <Observability />
                  </RequirePlatformRole>
                }
              />

              {/* Iter 91B.7 — Unified Platform Operations Log */}
              <Route
                path="/platform/operations-log"
                element={
                  <RequirePlatformRole>
                    <PlatformOperationsLog />
                  </RequirePlatformRole>
                }
              />

              {/* Iter 91B.9 — Platform Communications Console */}
              <Route
                path="/platform/communications"
                element={
                  <RequirePlatformRole>
                    <PlatformCommunications />
                  </RequirePlatformRole>
                }
              />
              <Route
                path="/platform/sms-packs"
                element={
                  <RequirePlatformRole>
                    <PlatformSmsPacks />
                  </RequirePlatformRole>
                }
              />
              <Route
                path="/platform/secrets"
                element={
                  <RequirePlatformRole>
                    <PlatformSecrets />
                  </RequirePlatformRole>
                }
              />

              {/* Enterprise — multi-location consolidated views.
                  Protected client-side by ModuleRouteGuard + server-side by
                  require_enterprise + require_module on the backend. */}
              <Route
                path="/enterprise/dashboard"
                element={
                  <ModuleRouteGuard module="multi_location_dashboard">
                    <EnterpriseDashboard />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/enterprise/locations"
                element={
                  <ModuleRouteGuard module="multi_location_dashboard">
                    <EnterpriseLocations />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/enterprise/benchmark"
                element={
                  <ModuleRouteGuard module="benchmark">
                    <EnterpriseBenchmark />
                  </ModuleRouteGuard>
                }
              />
              <Route
                path="/enterprise/audit-log"
                element={
                  <ModuleRouteGuard module="audit_trail_enterprise">
                    <EnterpriseAuditLog />
                  </ModuleRouteGuard>
                }
              />

              {/* AI Financial Controller — owner/manager/admin only */}
              <Route
                path="/ai-controller"
                element={
                  <ProtectedRoute
                    roles={[
                      "platform_owner",
                      "platform_admin",
                      "restaurant_owner",
                      "hotel_owner",
                      "restaurant_manager",
                      "hotel_manager",
                      "enterprise_owner",
                      "enterprise_admin",
                      "area_manager",
                    ]}
                  >
                    <ModuleRouteGuard module="ai_controller">
                      <AIController />
                    </ModuleRouteGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-controller/profit-recovery"
                element={
                  <ProtectedRoute
                    roles={[
                      "platform_owner",
                      "platform_admin",
                      "restaurant_owner",
                      "hotel_owner",
                      "restaurant_manager",
                      "hotel_manager",
                      "enterprise_owner",
                      "enterprise_admin",
                      "area_manager",
                    ]}
                  >
                    <ModuleRouteGuard module="profit_recovery">
                      <ProfitRecovery />
                    </ModuleRouteGuard>
                  </ProtectedRoute>
                }
              />

              {/* Placeholder modules — gracefully handle features in roadmap. */}
              <Route path="/modules/:moduleId" element={<PlaceholderModule />} />

              {/* Locked modules — feature gating page when plan doesn't include. */}
              <Route path="/locked/:moduleId" element={<LockedModulePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </ImpersonationProvider>
        </AccessMatrixProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
